import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import MessageBubble from "./components/MessageBubble";
import ChatInput from "./components/ChatInput";
import TypingIndicator from "./components/TypingIndicator";
import logoBlue from "../../assets/landing-page/logo-blue.svg";

const API_URL = "https://alyseiai.ibyteworkshop.com/chat";
const SESSION_KEY = "alysei_chat_session_id";

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-6 py-16">
    <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mb-5">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </div>
    <h2 className="text-lg font-bold text-gray-800 mb-2">How can I help you?</h2>
    <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
      Describe your sourcing needs and I'll match you with relevant Italian food & beverage producers.
    </p>
  </div>
);

const ErrorBanner = ({ error, onDismiss }) => {
  if (!error) return null;
  return (
    <div className="mx-4 mb-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span className="flex-1">{error}</span>
      <button onClick={onDismiss} className="ml-auto text-red-400 hover:text-red-600 font-bold text-base leading-none">×</button>
    </div>
  );
};

const ChatPage = () => {
  const history = useHistory();
  const location = useLocation();

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);

  const messagesEndRef = useRef(null);
  const firstMessageSent = useRef(false);

  // Scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Send a message to the API
  const sendMessage = useCallback(async (text, currentSessionId, isEdit = false, editIndex = null) => {
    const userMsg = {
      id: Date.now(),
      role: "user",
      text: text.trim(),
      timestamp: Date.now(),
    };

    setMessages((prev) => {
      if (isEdit && editIndex !== null) {
        // Replace edited message and remove all messages after it
        return [...prev.slice(0, editIndex), userMsg];
      }
      return [...prev, userMsg];
    });

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(API_URL, {
        session_id: currentSessionId || "",
        message: text.trim(),
      });

      const { session_id, reply } = response.data;

      // Persist session_id
      if (session_id) {
        setSessionId(session_id);
        sessionStorage.setItem(SESSION_KEY, session_id);
      }

      const aiMsg = {
        id: Date.now() + 1,
        role: "assistant",
        text: reply || "I'm sorry, I couldn't process your request.",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle initial message passed from homepage
  useEffect(() => {
    if (firstMessageSent.current) return;

    const initialMessage = location?.state?.initialMessage;
    if (initialMessage && initialMessage.trim()) {
      firstMessageSent.current = true;
      // Clear location state immediately so a page refresh never replays this message
      history.replace({ ...location, state: null });
      sendMessage(initialMessage, "");
    }
  }, [location, sendMessage, history]);

  const handleSend = () => {
    if (!inputValue.trim() || loading) return;

    if (editingMessage) {
      const idx = messages.findIndex((m) => m.id === editingMessage.id);
      sendMessage(inputValue, "", true, idx);
      setEditingMessage(null);
    } else {
      sendMessage(inputValue, sessionId);
    }

    setInputValue("");
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setInputValue(message.text);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setInputValue("");
  };

  const handleBack = () => {
    history.push("/");
  };

  return (
    <div className="flex flex-col h-screen bg-[#f7f8fa] overflow-hidden" style={{ fontFamily: "SF Pro Display, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── Header: Logo LEFT · Badge CENTER · Back RIGHT ── */}
      <header className="flex-shrink-0 sticky top-0 z-20 bg-white border-b border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
        <div className="relative flex items-center h-14 md:h-16 px-4 md:px-8">

          {/* LEFT — Logo */}
          <a href="/" className="flex items-center z-10">
            <img src={logoBlue} alt="Alysei" className="h-6 md:h-7" />
          </a>

          {/* CENTER — absolutely positioned so it's always truly centred */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1 rounded-full select-none">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              AI Online
            </div>
          </div>

          {/* RIGHT — Back to Home */}
          <button
            onClick={handleBack}
            className="ml-auto z-10 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors whitespace-nowrap"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Back</span>
          </button>
        </div>
      </header>

      {/* ── Subtitle ── */}
      <div className="flex-shrink-0 text-center py-2 border-b border-gray-100 bg-white/60">
        <span className="text-[11px] text-gray-400 tracking-wide select-none">
          Alysei AI · Italian Food &amp; Beverage Sourcing Assistant
        </span>
      </div>

      {/* ── Messages area ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-6 flex flex-col gap-5 min-h-full">
          {messages.length === 0 && !loading && <EmptyState />}

          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onEdit={handleEdit}
            />
          ))}

          {loading && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* ── Error banner ── */}
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* ── Input area ── */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        loading={loading}
        editingMessage={editingMessage}
        onCancelEdit={handleCancelEdit}
      />

      {/* Keyframe animations */}
      <style>{`
        @keyframes typing-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes alysei-fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: alysei-fadeIn 0.28s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default ChatPage;
